import { CircularProgress, Tooltip } from "@mui/material";
import clsx from "clsx";
import { Icon } from "components/Icon";
import * as MUIIcon from "react-icons/md";

type TProps = {
  title: string;
  value?: string | number;
  /** Optional unit rendered after the value (e.g. m³). */
  unit?: string;
  /**
   * How this figure is produced — shown behind an info icon next to the title.
   * Use it for anything derived (ratios, shares, averages) so the reader can
   * see the calculation instead of inferring it.
   */
  hint?: string;
  /** Muted one-liner under the value, e.g. the formula in short form. */
  caption?: string;
  iconName?: keyof typeof MUIIcon;
  isLoading?: boolean;
  className?: string;
  iconClassName?: string;
};

const formatValue = (value?: string | number) => {
  if (value === undefined || value === null) return "-";
  if (typeof value === "number") return value.toLocaleString("fa-IR");
  return value;
};

/** Small stat card used on the report dashboards. */
export function KpiCard({
  title,
  value,
  unit,
  hint,
  caption,
  iconName,
  isLoading,
  className,
  iconClassName,
}: TProps) {
  return (
    <div
      className={clsx(
        "rounded-md bg-white p-4 flex items-center gap-3",
        className
      )}
    >
      {iconName && (
        <div
          className={clsx(
            "w-10 h-10 rounded-full bg-primary-10 flex items-center justify-center shrink-0",
            iconClassName
          )}
        >
          <Icon icon={iconName} className="text-xl text-primary-90" />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Titles wrap to two lines rather than truncating on the first — these
            labels are long in Persian and a bare "…" tells the reader nothing.
            The native title attribute still carries the full string for the
            rare case where even two lines are not enough. */}
        <span className="flex items-start gap-1 text-xs text-gray-90">
          <span className="line-clamp-2 break-words" title={title}>
            {title}
          </span>
          {hint && (
            <Tooltip title={hint} arrow placement="top">
              <span className="flex shrink-0 items-center text-gray-70">
                <Icon icon="MdInfoOutline" className="text-sm" />
              </span>
            </Tooltip>
          )}
        </span>
        {isLoading ? (
          <CircularProgress size={18} />
        ) : (
          <span className="text-lg font-bold text-gray-130 whitespace-nowrap">
            {formatValue(value)}
            {unit && (
              <span className="text-xs font-normal text-gray-90 mr-1">
                {unit}
              </span>
            )}
          </span>
        )}
        {caption && !isLoading && (
          <span className="text-tiny text-gray-70" title={caption}>
            {caption}
          </span>
        )}
      </div>
    </div>
  );
}

export default KpiCard;
